// src/admin/FormEditor/FormEditor.jsx
// Main form editor component that orchestrates all sub-components
// Features:
// - Form metadata management
// - Multi-language support
// - Image uploads (logo/banner)
// - Rich text editing for descriptions
// - Question list management
// - Form validation
// - Acknowledgment settings

import {
  Calendar,
  FileText,
  Image as ImageIcon,
  ListTodo,
  Plus,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"; // Add this

import { PREFIX } from "../../router.jsx"; // Import the PREFIX constant
import { CollapsibleSection } from "./components/CollapsibleSection.jsx";
import { DraggableList } from "./components/DraggableList.jsx";
import FormAvailabilityPeriod from "./components/FormAvailabilityPeriod.jsx";
import FormHeader from "./components/FormHeader/FormHeader.jsx";
import FormMetadataPanel from "./components/FormMetadataPanel.jsx";
import FormReportSection from "./components/FormReportSection.jsx";
import ImageUploader from "./components/ImageUploader.jsx";
import PICSEditor from "./components/PICSEditor";
import QuestionEditor from "./components/QuestionEditor.jsx";
import { RichTextEditor } from "./components/RichTextEditor.jsx";

import { DRAFT_LOAD_FORM_URL } from "../../constants/urls.js";

import {
  addQuestion,
  reorderQuestions,
  updateFormMetadata,
} from "./store/formEditorSlice.js";
import {
  createTemplateQuestion,
  migrateFormStructure,
  prepareNewFormMetadata,
} from "./utils/formTemplates.js";

import { Mail, MapPin, Phone } from "lucide-react";

const QuickAddQuestionButton = ({
  icon: Icon,
  label,
  onClick,
  className = "",
}) => (
  <button
    onClick={onClick}
    className={`flex items-center px-3 py-2 text-sm border rounded-md hover:bg-gray-50 ${className}`}
  >
    <Icon size={16} className="mr-2" />
    {label}
  </button>
);

const FormEditor = ({ mode = "form", subform = false }) => {
  const [questionsCollapseVersion, setQuestionsCollapseVersion] = useState(0);

  // console.log('FormEditor initialized with mode:', mode);
  const [error, setError] = useState(null);

  const sectionClass = "border-b last:border-b-0 p-2"; // Base section padding
  const { formId } = useParams();

  const defaultFormMetadata = prepareNewFormMetadata();
  const dispatch = useDispatch();
  const { formMetadata } = useSelector((state) => state.formEditor);
  const [loading, setLoading] = useState(true); // Add loading state

  // Add state for Questions section collapse
  const [isQuestionsCollapsed, setIsQuestionsCollapsed] = useState(() => {
    const saved = localStorage.getItem("form-editor-questions-collapsed");
    return saved ? JSON.parse(saved) : false;
  });

  // Add effect to persist collapse state
  useEffect(() => {
    localStorage.setItem(
      "form-editor-questions-collapsed",
      JSON.stringify(isQuestionsCollapsed)
    );
  }, [isQuestionsCollapsed]);

  // Add these functions at the top of the FormEditor component
  const handleExpandAllQuestions = () => {
    formMetadata.questions.forEach((_, index) => {
      localStorage.setItem(
        `form-editor-question-${index}-collapsed`,
        JSON.stringify(false)
      );
    });
    // Increment version to trigger re-render
    setQuestionsCollapseVersion((v) => v + 1);
  };

  const handleCollapseAllQuestions = () => {
    formMetadata.questions.forEach((_, index) => {
      localStorage.setItem(
        `form-editor-question-${index}-collapsed`,
        JSON.stringify(true)
      );
    });
    // Increment version to trigger re-render
    setQuestionsCollapseVersion((v) => v + 1);
  };

  const handleReorderQuestions = useCallback(
    (newQuestions) => {
      dispatch(reorderQuestions(newQuestions));
    },
    [dispatch]
  );

  const handleFormMetaChange = (e) => {
    const field = e.target.getAttribute("field");
    const updates = {};
    if (e.target.type === "checkbox") {
      updates[field] = e.target.checked;
    } else if (e.target.type === "number") {
      updates[field] = e.target.value === "" ? null : parseInt(e.target.value);
    } else {
      updates[field] = e.target.value;
    }
    dispatch(updateFormMetadata({ updates }));
  };

  const handleFormMetaChangeByLanguage2 = (field, lang, value) => {
    const updates = {};
    updates[field] = { ...formMetadata[field] };
    updates[field][lang] = value;
    dispatch(updateFormMetadata({ updates }));
  };

  const handleFormMetaChangeByLanguage = (e) => {
    const field = e.target.getAttribute("field");
    const lang = e.target.getAttribute("langcode");
    const updates = {};
    updates[field] = { ...formMetadata[field] };

    if (e.target.type === "checkbox") {
      updates[field][lang] = e.target.checked;
    } else if (e.target.type === "number") {
      updates[field][lang] =
        e.target.value === "" ? null : parseInt(e.target.value);
    } else {
      updates[field][lang] = e.target.value;
    }
    dispatch(updateFormMetadata({ updates }));
  };

  useEffect(() => {
    const loadFormData = async () => {
      if (!formId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        // console.log(`Starting to load form data in mode:`, mode); // Debug log

        if (mode === "draft") {
          const draftUrl = subform
            ? `${DRAFT_LOAD_FORM_URL}/${formId}?subform=true`
            : `${DRAFT_LOAD_FORM_URL}/${formId}`;
          console.log("Fetching draft from:", draftUrl); // Debug log

          const response = await fetch(draftUrl);
          if (!response.ok) {
            throw new Error("Failed to load draft");
          }
          const data = await response.json();
          if (!data.draft_data) {
            throw new Error("Draft data is missing or invalid");
          }
          let formData = data.draft_data;
          formData = migrateFormStructure(formData);
          const updates = { ...formData };
          dispatch(updateFormMetadata({ updates }));
          console.log("Draft data loaded successfully"); // Debug log
        } else {
          const formUrl = subform
            ? `${PREFIX}/forms/subforms/${formId}.json`
            : `${PREFIX}/forms/${formId}.json`;
          console.log("Fetching form from:", formUrl); // Debug log

          const response = await fetch(formUrl);
          if (!response.ok) {
            throw new Error("Failed to load form");
          }
          let formData = await response.json();
          formData = migrateFormStructure(formData);
          const updates = { ...formData };
          dispatch(updateFormMetadata({ updates }));
          console.log("Form data loaded successfully"); // Debug log
        }
      } catch (error) {
        console.error("Error loading form:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [formId, mode, dispatch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full mx-auto p-6 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Failed to Load Form
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className={`${sectionClass} bg-red-50 space-y-2`}>
        <FormHeader />
      </div>

      {!formMetadata.subform && (
        <>
          {/* Form Report Meta */}
          <CollapsibleSection
            id="form-report"
            title="Form Report"
            description="Configure report settings and display options"
            icon={FileText}
            bgColor="bg-green-50"
            borderColor="border-green-100"
            defaultCollapsed={false}
          >
            <FormReportSection />
          </CollapsibleSection>

          {/* Form Availability Period */}
          <CollapsibleSection
            id="form-availability"
            title="Form Availability Period"
            icon={Calendar}
            bgColor="bg-red-50"
            borderColor="border-red-100"
            summary={
              <div className="text-sm text-gray-600 mt-0.5">
                {formMetadata.valid_from.time || formMetadata.valid_to.time ? (
                  <div className="space-y-1">
                    <span className="flex items-center gap-1">
                      {formMetadata.valid_from.time && (
                        <span className="text-blue-600">
                          From:{" "}
                          {new Date(
                            formMetadata.valid_from.time
                          ).toLocaleString()}
                        </span>
                      )}
                      {formMetadata.valid_from.time &&
                        formMetadata.valid_to.time && (
                          <span className="text-gray-400 mx-1">•</span>
                        )}
                      {formMetadata.valid_to.time && (
                        <span className="text-blue-600">
                          To:{" "}
                          {new Date(
                            formMetadata.valid_to.time
                          ).toLocaleString()}
                        </span>
                      )}
                    </span>
                    {/* Status indicator */}
                    {(() => {
                      const now = new Date();
                      const start = formMetadata.valid_from.time
                        ? new Date(formMetadata.valid_from.time)
                        : null;
                      const end = formMetadata.valid_to.time
                        ? new Date(formMetadata.valid_to.time)
                        : null;

                      if (!start && !end) return null;

                      const isActive =
                        (!start || now >= start) && (!end || now <= end);

                      return (
                        <span
                          className={`inline-flex items-center gap-1 text-xs ${
                            isActive ? "text-green-600" : "text-orange-600"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isActive ? "bg-green-600" : "bg-orange-600"
                            }`}
                          />
                          {isActive ? "Currently Active" : "Not Active"}
                        </span>
                      );
                    })()}
                  </div>
                ) : (
                  <span className="text-gray-500">
                    No availability period set
                  </span>
                )}
              </div>
            }
            defaultCollapsed={false}
          >
            <FormAvailabilityPeriod />
          </CollapsibleSection>

          {/* Logo Upload */}
          <CollapsibleSection
            id="form-logo"
            title="Form Logo"
            icon={ImageIcon}
            bgColor="bg-green-50"
            borderColor="border-green-100"
            summary={
              <div className="flex items-center justify-between w-full">
                <p className="text-sm text-gray-600 mt-0.5">
                  {Object.values(formMetadata.logo_url || {}).some(
                    (url) => url
                  ) ||
                  Object.values(formMetadata.logo_base64 || {}).some(
                    (base64) => base64
                  ) ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <span>✓</span> Logo uploaded for{" "}
                      {
                        [
                          ...new Set([
                            ...Object.entries(formMetadata.logo_url || {})
                              .filter(([_, url]) => url)
                              .map(([lang]) => lang),
                            ...Object.entries(formMetadata.logo_base64 || {})
                              .filter(([_, base64]) => base64)
                              .map(([lang]) => lang),
                          ]),
                        ].length
                      }{" "}
                      language(s)
                    </span>
                  ) : (
                    <span className="text-gray-500">No logo uploaded</span>
                  )}
                </p>

                {/* Logo previews */}
                {(formMetadata.logo_url || formMetadata.logo_base64) && (
                  <div className="flex items-center gap-3">
                    {[
                      ...new Set([
                        ...Object.entries(formMetadata.logo_url || {})
                          .filter(([_, url]) => url)
                          .map(([lang, url]) => ({ lang, value: url })),
                        ...Object.entries(formMetadata.logo_base64 || {})
                          .filter(([_, base64]) => base64)
                          .map(([lang, base64]) => ({ lang, value: base64 })),
                      ]),
                    ].map(({ lang, value }) => (
                      <div key={lang} className="relative group">
                        <div className="w-12 h-12 bg-white rounded-lg overflow-hidden border border-green-100 p-1">
                          <img
                            src={value}
                            alt={`Logo for ${lang}`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          {lang.toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            }
            defaultCollapsed={true}
          >
            <ImageUploader type="logo" />
          </CollapsibleSection>

          {/* Banner Upload Areas */}
          <CollapsibleSection
            id="form-banner"
            title="Form Banner"
            icon={ImageIcon}
            bgColor="bg-red-50"
            borderColor="border-red-100"
            summary={
              <p className="text-sm text-gray-600 mt-0.5">
                {Object.values(formMetadata.banner_url || {}).some(
                  (url) => url
                ) ||
                Object.values(formMetadata.banner_base64 || {}).some(
                  (base64) => base64
                ) ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <span>✓</span> Banner uploaded for{" "}
                    {
                      [
                        ...new Set([
                          ...Object.entries(formMetadata.banner_url || {})
                            .filter(([_, url]) => url)
                            .map(([lang]) => lang),
                          ...Object.entries(formMetadata.banner_base64 || {})
                            .filter(([_, base64]) => base64)
                            .map(([lang]) => lang),
                        ]),
                      ].length
                    }{" "}
                    language(s)
                  </span>
                ) : (
                  <span className="text-gray-500">No banner uploaded</span>
                )}
              </p>
            }
            defaultCollapsed={true}
          >
            <ImageUploader type="banner" />
          </CollapsibleSection>

          {/* Basic Info */}
          <CollapsibleSection
            id="basic-info"
            title="Basic Info"
            icon={FileText}
            bgColor="bg-green-50"
            borderColor="border-green-100"
            summary={
              <div className="text-sm text-gray-600 mt-0.5">
                <span className="inline-flex items-center gap-2">
                  {formMetadata.event_name[formMetadata.default_language] && (
                    <span className="text-blue-600">
                      {formMetadata.event_name[formMetadata.default_language]}
                    </span>
                  )}
                  {formMetadata.contact_email && (
                    <span className="text-gray-500">
                      • {formMetadata.contact_email}
                    </span>
                  )}
                </span>
              </div>
            }
            defaultCollapsed={false}
          >
            {/* Event Name */}
            <div className="space-y-6">
              <div className="border-b border-green-100 pb-4">
                <div className="text-sm font-medium text-gray-700 mb-3">
                  Event Name
                </div>
                <div className="space-y-2">
                  {formMetadata.supported_languages.map((langcode) => (
                    <div key={langcode} className="flex items-center gap-3">
                      <div className="w-8 text-xs font-medium text-gray-500">
                        {langcode.toUpperCase()}
                      </div>
                      <input
                        type="text"
                        field="event_name"
                        langcode={langcode}
                        value={formMetadata.event_name[langcode] || ""}
                        onChange={handleFormMetaChangeByLanguage}
                        className="flex-1 border rounded-md p-2"
                        placeholder={`Event Name in ${langcode.toUpperCase()}`}
                        aria-label={`Event Name in ${langcode.toUpperCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Title */}
              <div className="border-b border-green-100 pb-4">
                <div className="text-sm font-medium text-gray-700 mb-3">
                  Form Title
                </div>
                <div className="space-y-2">
                  {formMetadata.supported_languages.map((langcode) => (
                    <div key={langcode} className="flex items-center gap-3">
                      <div className="w-8 text-xs font-medium text-gray-500">
                        {langcode.toUpperCase()}
                      </div>
                      <input
                        type="text"
                        field="title"
                        langcode={langcode}
                        value={formMetadata.title[langcode] || ""}
                        onChange={handleFormMetaChangeByLanguage}
                        className="flex-1 border rounded-md p-2"
                        placeholder={`Form title in ${langcode.toUpperCase()}`}
                        aria-label={`Form title in ${langcode.toUpperCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Email */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">
                  Contact Email
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8">
                    <Mail size={16} className="text-gray-500" />
                  </div>
                  <input
                    type="email"
                    field="contact_email"
                    value={formMetadata.contact_email || ""}
                    onChange={handleFormMetaChange}
                    className="flex-1 border rounded-md p-2"
                    placeholder="Enter contact email address"
                    aria-label="Contact email address"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Description inputs for all languages */}
          <CollapsibleSection
            id="form-description"
            title="Form Description"
            bgColor="bg-red-50"
            borderColor="border-red-100"
            summary={
              <p className="text-sm text-gray-600 mt-0.5">
                Available in {formMetadata.supported_languages.length} language
                {formMetadata.supported_languages.length !== 1 ? "s" : ""}:{" "}
                {formMetadata.supported_languages.map((lang) => (
                  <span
                    key={lang}
                    className={`inline-flex items-center ${
                      formMetadata.description[lang]
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  >
                    {lang.toUpperCase()}
                    {formMetadata.description[lang] && "✓"}
                    {lang !==
                      formMetadata.supported_languages[
                        formMetadata.supported_languages.length - 1
                      ] && ", "}
                  </span>
                ))}
              </p>
            }
            defaultCollapsed={false}
          >
            <div className="space-y-6">
              {formMetadata.supported_languages.map((langcode) => (
                <div key={langcode} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">
                      {langcode.toUpperCase()} Form Description
                    </div>
                    <div className="text-xs text-gray-500">
                      {formMetadata.description[langcode]?.length || 0}{" "}
                      characters
                    </div>
                  </div>
                  <RichTextEditor
                    value={formMetadata.description[langcode]}
                    onChange={(content) =>
                      handleFormMetaChangeByLanguage2(
                        "description",
                        langcode,
                        content
                      )
                    }
                    language={langcode}
                    fieldName="description"
                    collapsible={true}
                    defaultCollapsed={false}
                    forceReset={true}
                    title={`Form Description (${langcode})`}
                  />
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Details input for all languages */}
          <CollapsibleSection
            id="form-details"
            title="Form Details"
            bgColor="bg-green-50"
            borderColor="border-green-100"
            summary={
              <p className="text-sm text-gray-600 mt-0.5">
                Available in {formMetadata.supported_languages.length} language
                {formMetadata.supported_languages.length !== 1 ? "s" : ""}:{" "}
                {formMetadata.supported_languages
                  .map((lang) => lang.toUpperCase())
                  .join(", ")}
              </p>
            }
            defaultCollapsed={true}
          >
            <div className="space-y-4">
              {formMetadata.supported_languages.map((langcode) => (
                <div key={langcode} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">
                      {langcode.toUpperCase()} Form Details
                    </div>
                    <div className="text-xs text-gray-500">
                      {formMetadata.details[langcode]?.length || 0} characters
                    </div>
                  </div>
                  <RichTextEditor
                    value={formMetadata.details[langcode]}
                    onChange={(content) =>
                      handleFormMetaChangeByLanguage2(
                        "details",
                        langcode,
                        content
                      )
                    }
                    language={langcode}
                    fieldName="details"
                    collapsible={true}
                    defaultCollapsed={true}
                    title={`Form Details (${langcode.toUpperCase()})`}
                  />
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Acknowledgment Display Method */}
          <CollapsibleSection
            id="acknowledgment-display"
            title="Acknowledgment Display Method"
            bgColor="bg-red-50"
            borderColor="border-red-100"
            summary={
              <div className="text-sm text-gray-600 mt-0.5 flex items-center gap-2">
                <span className="font-medium">Method:</span>
                <span className="px-2 py-0.5 bg-white rounded-full text-blue-600 border border-blue-100">
                  {formMetadata.acknowledgmentDisplay === "belowSubmitButton"
                    ? "Below Form"
                    : formMetadata.acknowledgmentDisplay === "popup"
                    ? "Popup"
                    : formMetadata.acknowledgmentDisplay === "redirect"
                    ? "Redirect"
                    : "Not Set"}
                </span>
                {formMetadata.acknowledgmentDisplay === "redirect" &&
                  formMetadata.acknowledgmentRedirectUrl && (
                    <span className="text-gray-500">
                      → {formMetadata.acknowledgmentRedirectUrl}
                    </span>
                  )}
              </div>
            }
            defaultCollapsed={true}
          >
            <div className="space-y-6">
              {/* Display Method Options */}
              <div className="space-y-3 bg-white rounded-lg p-4 shadow-sm">
                <div className="text-sm font-medium text-gray-700 mb-3">
                  Select Display Method
                </div>

                <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-red-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="acknowledgmentDisplay"
                    field="acknowledgmentDisplay"
                    value="belowSubmitButton"
                    checked={
                      formMetadata.acknowledgmentDisplay === "belowSubmitButton"
                    }
                    onChange={handleFormMetaChange}
                    className="text-blue-600 w-4 h-4"
                  />
                  <div>
                    <div className="font-medium">Below Form</div>
                    <div className="text-sm text-gray-500">
                      Display message below the form with inputs disabled
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-red-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="acknowledgmentDisplay"
                    field="acknowledgmentDisplay"
                    value="popup"
                    checked={formMetadata.acknowledgmentDisplay === "popup"}
                    onChange={handleFormMetaChange}
                    className="text-blue-600 w-4 h-4"
                  />
                  <div>
                    <div className="font-medium">Popup Dialog</div>
                    <div className="text-sm text-gray-500">
                      Show message in a popup overlay
                    </div>
                  </div>
                </label>

                <div className="p-3 rounded-lg hover:bg-red-50 transition-colors">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="acknowledgmentDisplay"
                      field="acknowledgmentDisplay"
                      value="redirect"
                      checked={
                        formMetadata.acknowledgmentDisplay === "redirect"
                      }
                      onChange={handleFormMetaChange}
                      className="text-blue-600 w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Redirect</div>
                      <div className="text-sm text-gray-500">
                        Redirect to a custom URL after submission
                      </div>
                    </div>
                  </label>

                  {formMetadata.acknowledgmentDisplay === "redirect" && (
                    <div className="mt-3 ml-7">
                      <input
                        type="text"
                        field="acknowledgmentRedirectUrl"
                        value={formMetadata.acknowledgmentRedirectUrl || ""}
                        onChange={handleFormMetaChange}
                        className="w-full border rounded-lg p-2.5 bg-white"
                        placeholder="Enter the URL to redirect to after submission"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Acknowledgment Messages */}
              <div className="space-y-4 bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-700">
                    Acknowledgment Messages
                  </div>
                  <div className="text-xs text-gray-500">
                    Available in {formMetadata.supported_languages.length}{" "}
                    language(s)
                  </div>
                </div>

                <div className="space-y-4">
                  {formMetadata.supported_languages.map((langcode) => (
                    <div key={langcode} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-600">
                          {langcode.toUpperCase()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formMetadata.acknowledgment?.[langcode]?.length || 0}{" "}
                          characters
                        </div>
                      </div>
                      <textarea
                        field="acknowledgment"
                        value={formMetadata.acknowledgment?.[langcode] || ""}
                        langcode={langcode}
                        onChange={handleFormMetaChangeByLanguage}
                        className="w-full border rounded-lg p-3 min-h-[100px] bg-white/50"
                        placeholder={`Enter acknowledgment message in ${langcode.toUpperCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Personal Information Collection Statement */}
          <CollapsibleSection
            id="pics"
            title="Personal Information Collection Statement"
            bgColor="bg-green-50"
            borderColor="border-green-100"
            defaultCollapsed={true}
          >
            <PICSEditor />
          </CollapsibleSection>

          {/* Other Options Section */}
          <CollapsibleSection
            id="other-options"
            title="Other Options"
            bgColor="bg-red-50"
            borderColor="border-red-100"
            summary={
              <div className="text-sm text-gray-600 mt-0.5">
                <div className="flex flex-col gap-1">
                  <span className="inline-flex items-center gap-2">
                    {formMetadata.clearBtn ? (
                      <span className="text-green-600">
                        ✓ Clear button enabled
                      </span>
                    ) : (
                      <span className="text-gray-500">
                        Clear button disabled
                      </span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {formMetadata.sendEmail ?? true ? (
                      <>
                        <span className="text-green-600">
                          ✓ Email to applicant enabled
                        </span>
                        {formMetadata.sendEmailFieldName && (
                          <span className="text-blue-600 text-xs">
                            (Field: {formMetadata.sendEmailFieldName})
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">
                        Email to applicant disabled
                      </span>
                    )}
                  </span>
                </div>
              </div>
            }
            defaultCollapsed={true}
          >
            <div className="space-y-4">
              {/* Clear Button Option */}
              <div className="relative group">
                <label className="flex items-center gap-3 hover:bg-red-100/30 p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    field="clearBtn"
                    checked={formMetadata.clearBtn || false}
                    onChange={handleFormMetaChange}
                    className="text-blue-600"
                  />
                  <div>
                    <div className="font-medium">Clear Button</div>
                    <div className="text-sm text-gray-600">
                      Show a button that allows users to clear all form inputs
                    </div>
                  </div>
                </label>
              </div>

              {/* Email Acknowledgement to applicant */}
              <div className="relative group bg-red-50 p-2">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      field="sendEmail"
                      checked={formMetadata.sendEmail ?? true}
                      onChange={handleFormMetaChange}
                      className="text-blue-600"
                    />
                    Send Email to Applicant
                  </label>
                  {formMetadata.sendEmail && (
                    <input
                      type="text"
                      field="sendEmailFieldName"
                      value={formMetadata.sendEmailFieldName || ""}
                      onChange={handleFormMetaChange}
                      className="flex-1 border rounded-md p-2 max-w-md"
                      placeholder="Enter field name (eg. email)"
                    />
                  )}
                </div>

                <div className="absolute hidden group-hover:block bg-gray-700 text-white text-xs rounded p-2 tooltip">
                  Send email to applicant when submit
                </div>
              </div>
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* Questions */}
      <CollapsibleSection
        id="questions"
        title={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <ListTodo size={20} className="text-gray-500" />
              <span>Questions</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandAllQuestions();
                }}
                className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              >
                Expand All
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCollapseAllQuestions();
                }}
                className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              >
                Collapse All
              </button>
            </div>
          </div>
        }
        bgColor="bg-white"
        borderColor="border-gray-200"
        summary={
          <p className="text-sm text-gray-600 mt-0.5">
            {formMetadata.questions.length} question
            {formMetadata.questions.length !== 1 ? "s" : ""} configured
            {formMetadata.questions.length > 0 && (
              <span className="text-xs text-gray-500 ml-2">
                ({formMetadata.questions.filter((q) => q.required).length}{" "}
                required)
              </span>
            )}
          </p>
        }
      >
        <div className="p-2">
          <DraggableList
            items={formMetadata.questions}
            onReorder={handleReorderQuestions}
            renderItem={(question, index) => (
              <QuestionEditor
                key={`${question.object_id}-${questionsCollapseVersion}`}
                questionIndex={index}
              />
            )}
          />

          <button
            onClick={() => dispatch(addQuestion({ question: null }))}
            className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg
           text-gray-500 hover:border-blue-500 hover:text-blue-500
           flex items-center justify-center"
          >
            <Plus size={20} className="mr-2" />
            Add Question
          </button>

          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <QuickAddQuestionButton
                icon={Mail}
                label="Participant"
                onClick={() => {
                  dispatch(
                    addQuestion({
                      question: createTemplateQuestion("salutation"),
                    })
                  );

                  dispatch(
                    addQuestion({
                      question: createTemplateQuestion("surname"),
                    })
                  );

                  dispatch(
                    addQuestion({
                      question: createTemplateQuestion("given_name"),
                    })
                  );
                }}
              />

              <QuickAddQuestionButton
                icon={MapPin}
                label="Email"
                onClick={() =>
                  dispatch(
                    addQuestion({
                      question: createTemplateQuestion("email"),
                    })
                  )
                }
              />

              <QuickAddQuestionButton
                icon={Phone}
                label="Phone"
                onClick={() =>
                  dispatch(
                    addQuestion({
                      question: createTemplateQuestion("phone"),
                    })
                  )
                }
              />
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <FormMetadataPanel formMetadata={formMetadata} />
    </div>
  );
};

export default FormEditor;
